import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Route,
  Routes,
  NavLink,
  Navigate,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { QueryClientProvider, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { LayoutDashboard, Inbox, CreditCard, LogOut, PlayCircle, RefreshCw } from 'lucide-react';
import { AppShell } from '@avagency/frontend/components/shell/AppShell';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Toaster } from '@avagency/frontend/components/ui/sonner';
import { createQueryClient } from '@avagency/frontend/lib/api';

import { Scorecard } from './pages/Scorecard';
import { ArchiveQueue } from './pages/ArchiveQueue';
import { Billing } from './pages/Billing';
import { SignIn } from './pages/SignIn';
import { ConnectMailchimp } from './pages/ConnectMailchimp';
import {
  AUTH_INVALIDATE_EVENT,
  clearOAuthCallbackStatusFromURL,
  fetchIntegrations,
  fetchMe,
  fetchScans,
  triggerScan,
  parseOAuthCallbackStatus,
  signOut,
  type IntegrationListItem,
  type IntegrationListResponse,
  type Me,
  type OAuthCallbackStatus,
  type ScanListItem,
} from './api';
import './index.css';

// Kit createQueryClient defaults: staleTime 30s, no refetchOnWindowFocus,
// retry up to 2 for non-4xx, no retry for 4xx. Per-query overrides live in
// useMe/useIntegrations for auth-critical state.
const queryClient = createQueryClient();

// ── useMe: strict-freshness auth gate ────────────────────────────────────────
// staleTime: 0 + refetchOnWindowFocus ensures re-auth checks on tab focus.
// A 401 is the "no session" signal; we short-circuit the retry loop on it.
function useMe() {
  return useQuery<Me, Error & { status?: number }>({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: (failureCount, err) => {
      if (err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

const MAILCHIMP_PROVIDER = 'mailchimp';

function useIntegrations(tenantID: string) {
  return useQuery<IntegrationListResponse, Error & { status?: number }>({
    queryKey: ['list_hygiene', 'integrations', tenantID],
    queryFn: fetchIntegrations,
  });
}

function pickPrimaryActive(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  return list.find((i) => i.provider === MAILCHIMP_PROVIDER && i.status === 'active');
}

function pickExistingMailchimp(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  // Covers active, reauth_required, and error states — anything the user should know about.
  return list.find((i) => i.provider === MAILCHIMP_PROVIDER);
}

// ── App: auth gate ────────────────────────────────────────────────────────────

function App() {
  const me = useMe();
  const qc = useQueryClient();

  // Auth invalidation: any product API 401 fires AUTH_INVALIDATE_EVENT →
  // we invalidate the auth query so the gate re-checks /me.
  useEffect(() => {
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(AUTH_INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(AUTH_INVALIDATE_EVENT, handler);
  }, [qc]);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (me.isError) {
    if (me.error.status === 401) {
      return <SignIn returnTo={window.location.pathname + window.location.search} />;
    }
    return <SignIn error={`Auth check failed: ${me.error.message}`} />;
  }
  return <AuthenticatedApp session={me.data!} />;
}

// ── AuthenticatedApp: integration gate ───────────────────────────────────────
// Branch precedence:
//   1. integrations loading → skeleton
//   2. integrations error → ConnectMailchimp with error banner
//   3. active Mailchimp integration → ConnectedShell
//   4. non-active or no integration → ConnectMailchimp (onboarding / reauth)

function AuthenticatedApp({ session }: { session: Me }) {
  const integrations = useIntegrations(session.tenant_id);
  const qc = useQueryClient();

  const [callbackStatus] = React.useState<OAuthCallbackStatus>(parseOAuthCallbackStatus);
  React.useEffect(() => {
    clearOAuthCallbackStatusFromURL();
  }, []);

  if (integrations.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }

  if (integrations.isError) {
    return (
      <ConnectMailchimp
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={{ kind: 'error', message: integrations.error.message }}
        onConnected={() => qc.invalidateQueries({ queryKey: ['list_hygiene', 'integrations', session.tenant_id] })}
        onDisconnected={() => qc.invalidateQueries({ queryKey: ['list_hygiene', 'integrations', session.tenant_id] })}
      />
    );
  }

  const active = pickPrimaryActive(integrations.data?.integrations);
  const existing = pickExistingMailchimp(integrations.data?.integrations);

  if (!active) {
    return (
      <ConnectMailchimp
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={callbackStatus}
        existingIntegration={existing}
        onConnected={() => qc.invalidateQueries({ queryKey: ['list_hygiene', 'integrations', session.tenant_id] })}
        onDisconnected={() => qc.invalidateQueries({ queryKey: ['list_hygiene', 'integrations', session.tenant_id] })}
      />
    );
  }

  return <ConnectedShell session={session} activeIntegration={active} />;
}

// ── ConnectedShell: app with sidebar + routes ─────────────────────────────────

function ConnectedShell({ session, activeIntegration }: { session: Me; activeIntegration: IntegrationListItem }) {
  useEffect(() => {
    document.title = 'Listfit';
  }, []);

  return (
    <BrowserRouter>
      <AppShell
        productSlug="listfit"
        topbar={<TopbarBrand email={session.email} />}
        sidebar={<Sidebar />}
      >
        <Routes>
          {/* Scorecard: keyed by scanId param */}
          <Route path="/scorecard/:scanId" element={<ScorecardRoute />} />
          {/* Archive queue: keyed by scanId param */}
          <Route path="/archive/:scanId" element={<ArchiveQueueRoute />} />
          {/* Billing / plan */}
          <Route path="/billing" element={<Billing tenantId={session.tenant_id} email={session.email} />} />
          {/* Dashboard: scan list + trigger (P8.5) */}
          <Route
            path="/"
            element={
              <Dashboard integration={activeIntegration} tenantID={session.tenant_id} />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

// Route shims — extract the scanId param and pass to page components.
function ScorecardRoute() {
  const { scanId } = useParams<{ scanId: string }>();
  if (!scanId) return <Navigate to="/" replace />;
  return <Scorecard scanId={scanId} />;
}

function ArchiveQueueRoute() {
  const { scanId } = useParams<{ scanId: string }>();
  if (!scanId) return <Navigate to="/" replace />;
  return <ArchiveQueue scanId={scanId} />;
}

// ── Dashboard: scan list + "Run scan" trigger (P8.5) ─────────────────────────
//
// The dashboard fetches the tenant's scan history. If scans exist, it shows
// them in a list with links to scorecards. A "Run scan" button triggers a new
// scan. For the first scan (no history), the user provides their Mailchimp
// list id (the audience/list identifier, e.g. "abc123def456") — subsequent
// scans default to the most recent list id.

interface DashboardProps {
  integration: IntegrationListItem;
  tenantID: string;
}

function Dashboard({ integration, tenantID }: DashboardProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['list_hygiene', 'scans', tenantID],
    queryFn: fetchScans,
    refetchInterval: 10000, // poll every 10s to pick up running→completed transitions
  });

  // For "first scan" flow: show list-id input
  const [listIdInput, setListIdInput] = React.useState('');
  const [showFirstScanHelp, setShowFirstScanHelp] = React.useState(false);
  const [triggerError, setTriggerError] = React.useState<string | null>(null);

  const scans = data?.scans ?? [];
  const lastListId = scans[0]?.esp_list_id ?? '';

  const trigger = useMutation({
    mutationFn: (espListID: string) =>
      triggerScan({ integration_id: integration.id, esp_list_id: espListID }),
    onSuccess: (resp) => {
      setTriggerError(null);
      qc.invalidateQueries({ queryKey: ['list_hygiene', 'scans', tenantID] });
      // If server returned a scan_id for an in-flight scan, navigate to it.
      if (resp.scan_id) {
        navigate(`/scorecard/${resp.scan_id}`);
      }
    },
    onError: (err: Error & { status?: number }) => {
      const msg =
        err.status === 404
          ? 'Integration not found or not active.'
          : err.status === 400
          ? 'Invalid list id or integration id. Check your Mailchimp audience id.'
          : 'Failed to trigger scan. Please try again.';
      setTriggerError(msg);
    },
  });

  const handleRunScan = () => {
    const listId = lastListId || listIdInput.trim();
    if (!listId) {
      setShowFirstScanHelp(true);
      return;
    }
    setTriggerError(null);
    trigger.mutate(listId);
  };

  return (
    <div className="lf-page-enter space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="lf-page-title text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scan history for{' '}
            <span className="font-medium text-foreground/80">
              {integration.display_name || 'your Mailchimp account'}
            </span>
          </p>
        </div>
        <Button
          onClick={handleRunScan}
          disabled={trigger.isPending || (!lastListId && !listIdInput.trim())}
          aria-busy={trigger.isPending}
          style={{ boxShadow: '0 0 16px rgba(79,134,247,0.2)' }}
        >
          {trigger.isPending ? (
            <>
              <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              Starting…
            </>
          ) : (
            <>
              <PlayCircle className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Run scan
            </>
          )}
        </Button>
      </div>

      {/* First-scan list-id input */}
      {scans.length === 0 && (
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-5 py-4 space-y-3">
          <p className="text-sm font-medium">Enter your Mailchimp audience/list ID to run the first scan.</p>
          <p className="text-xs text-muted-foreground">
            Find it in Mailchimp → Audience → Manage Audience → Settings → Audience name and defaults.
            It looks like <code className="font-mono">abc123def456</code>.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={listIdInput}
              onChange={(e) => {
                setListIdInput(e.target.value);
                setShowFirstScanHelp(false);
                setTriggerError(null);
              }}
              placeholder="abc123def456"
              className="flex-1 rounded-lg border border-white/[0.10] bg-white/[0.05] px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-colors"
            />
            <Button
              size="sm"
              onClick={() => {
                if (!listIdInput.trim()) return;
                trigger.mutate(listIdInput.trim());
              }}
              disabled={!listIdInput.trim() || trigger.isPending}
            >
              Run
            </Button>
          </div>
          {(showFirstScanHelp || triggerError) && (
            <p role="alert" className="text-xs text-orange-300">
              {triggerError ?? 'Please enter a Mailchimp audience/list ID first.'}
            </p>
          )}
        </div>
      )}

      {/* Trigger error for subsequent scans */}
      {scans.length > 0 && triggerError && (
        <p role="alert" className="text-xs text-orange-300">{triggerError}</p>
      )}

      {/* Scan list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded-lg border border-white/[0.06] bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : scans.length > 0 ? (
        <div className="space-y-2">
          {scans.map((scan) => (
            <ScanListRow key={scan.scan_id} scan={scan} onOpen={() => navigate(`/scorecard/${scan.scan_id}`)} />
          ))}
        </div>
      ) : (
        <div className="lf-page-enter flex flex-col items-center justify-center min-h-[30vh] gap-3 text-center p-8">
          <div className="lf-empty-ring" style={{ width: 64, height: 64 }} aria-hidden="true">
            <LayoutDashboard className="h-5 w-5 text-primary/70" />
          </div>
          <p className="text-sm font-medium">No scans yet</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Enter your Mailchimp audience/list ID above and click Run to score your first list.
          </p>
        </div>
      )}
    </div>
  );
}

interface ScanListRowProps {
  scan: ScanListItem;
  onOpen: () => void;
}

function ScanListRow({ scan, onOpen }: ScanListRowProps) {
  const statusClass =
    scan.status === 'completed'
      ? 'text-green-400'
      : scan.status === 'running'
      ? 'text-yellow-400'
      : 'text-red-400';

  return (
    <button
      onClick={scan.status === 'completed' ? onOpen : undefined}
      disabled={scan.status !== 'completed'}
      className={[
        'w-full text-left flex items-center justify-between gap-4 px-4 py-3',
        'rounded-lg border border-white/[0.06] bg-white/[0.02]',
        'transition-colors duration-150',
        scan.status === 'completed'
          ? 'hover:bg-white/[0.05] cursor-pointer'
          : 'cursor-default opacity-80',
      ].join(' ')}
      aria-label={`Scan from ${new Date(scan.started_at).toLocaleDateString()} — ${scan.status}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div>
          <p className="text-sm font-medium truncate">
            {new Date(scan.started_at).toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground/70 font-mono truncate">
            list: {scan.esp_list_id}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0 text-xs text-right">
        <span className={statusClass + ' font-medium'}>{scan.status}</span>
        {scan.status === 'completed' && (
          <span className="text-muted-foreground">
            {scan.scored_count.toLocaleString()} scored
          </span>
        )}
      </div>
    </button>
  );
}

// ── Topbar ────────────────────────────────────────────────────────────────────

function TopbarBrand({ email }: { email: string }) {
  return (
    <div className="flex w-full items-center gap-3">
      <div
        className="lf-brand-orb flex-shrink-0"
        style={{ width: 24, height: 24 }}
        aria-hidden="true"
      />
      <span className="text-sm font-semibold tracking-tight">Listfit</span>
      <span className="hidden text-xs text-muted-foreground/70 sm:inline">
        Email list hygiene
      </span>
      <div className="ml-auto flex items-center gap-3">
        <span className="hidden text-xs text-muted-foreground sm:inline">{email}</span>
        <Button variant="outline" size="sm" onClick={() => signOut('/')}>
          <LogOut className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar() {
  const linkClass = (isActive: boolean) =>
    [
      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
      isActive
        ? 'bg-primary/15 text-primary border border-primary/20 shadow-[0_0_12px_rgba(79,134,247,0.12)]'
        : 'text-foreground/70 hover:bg-white/5 hover:text-foreground border border-transparent',
    ].join(' ');

  return (
    <ul className="space-y-1">
      <li>
        <NavLink to="/" end className={({ isActive }) => linkClass(isActive)}>
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </NavLink>
      </li>
      <li>
        <NavLink to="/billing" className={({ isActive }) => linkClass(isActive)}>
          <CreditCard className="h-4 w-4" />
          Billing
        </NavLink>
      </li>
      {/* Archive queue is accessible via /archive/:scanId from the scorecard */}
      <li>
        <NavLink
          to="/archive"
          className={({ isActive }) => linkClass(isActive)}
          // This link is present for navigational orientation; in practice
          // users arrive via Scorecard → "Review archive queue" button.
          aria-label="Archive queue (navigate from scorecard)"
        >
          <Inbox className="h-4 w-4" />
          Archive queue
        </NavLink>
      </li>
    </ul>
  );
}

// ── Mount ─────────────────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  </React.StrictMode>,
);
